import { createOpenAI } from '@ai-sdk/openai';
import { generateText } from 'ai';

const TRANSLATE_TIMEOUT_MS = 30_000;

const openai_openrouter = createOpenAI({
    baseURL: 'https://openrouter.ai/api/v1',
    apiKey: process.env.OPENROUTER_API_KEY,
});
const TRANSLATE_MODEL = openai_openrouter('google/gemini-3.7-flash');

/**
 * 通用提示词翻译 system prompt（目标语言由 user prompt 指定）。
 */
const TRANSLATE_PROMPT_SYSTEM = [
    'You are a professional prompt translator.',
    'Translate the user content to the target language while preserving meaning, tone, structure, and any placeholders.',
    'Auto-detect the source language.',
    'Do not add explanations. Output only the translated text.',
].join('\n');

/**
 * 固定翻译为英文的 system prompt（用于生成模型提示词英文化）。
 */
const TRANSLATE_TO_ENGLISH_SYSTEM = [
    'You are a professional translator for AI image/video generation prompts.',
    'Translate the user content into natural, fluent English suitable for generative models.',
    'Preserve meaning, tone, structure, camera language, style cues, and any placeholders, variables, code, URLs, or special tokens.',
    'Auto-detect the source language. If the text is already English, return it unchanged.',
    'Do not add explanations, quotes, or extra formatting. Output only the translated English text.',
].join('\n');

/**
 * 使用 LLM 翻译提示词。
 * - 保留变量/占位符/代码/URL 结构与语义
 * - 仅返回译文，不添加解释
 */
async function translatePrompt(
    input: string,
    to: string = 'en'
): Promise<string> {
    const prompt = [
        `Target language: ${to}`,
        `Text:'${input}'`,
    ].filter(Boolean).join('\n');

    const { text } = await generateText({
        model: TRANSLATE_MODEL,
        temperature: 0,
        system: TRANSLATE_PROMPT_SYSTEM,
        prompt: prompt,
        abortSignal: AbortSignal.timeout(TRANSLATE_TIMEOUT_MS),
    });

    return text.trim();
}

/**
 * 翻译（返回耗时）
 */
export async function translatePromptWithTiming(
    input: string,
    to: string = 'en'
): Promise<{ text: string; ms: number }> {
    const start = Date.now();
    const text = await translatePrompt(input, to);
    const ms = Date.now() - start;
    return { text, ms };
}

/** 便捷函数：翻译为英文（固定英译提示词） */
export async function translateToEnglish(input: string): Promise<string> {
    const trimmed = (input ?? '').trim();
    if (!trimmed) return '';

    const { text } = await generateText({
        model: TRANSLATE_MODEL,
        temperature: 0,
        system: TRANSLATE_TO_ENGLISH_SYSTEM,
        prompt: trimmed,
        abortSignal: AbortSignal.timeout(TRANSLATE_TIMEOUT_MS),
    });

    return text.trim();
}

// 语言无关的错误改写提示词：用元指令描述「分类 + 应输出的含义」，最终用 {{lang}} 决定输出语言，
// 因此同一套规则可产出中文 / 英文等任意目标语言的客服话术（不再写死中文成品句）。
export const DEFAULT_ERROR_TRANSLATE_PROMPT = [
    "You are a product-facing error message rewriter.",
    "Convert a technical error message into ONE clear, friendly, user-facing sentence.",
    "Strict rules:",
    "- Output ONLY the final message. No extra text, no quotes, no explanations.",
    "- Always desensitize: never expose stack traces, keys, internal IDs, or raw provider payloads.",
    "- Classify the error and produce the matching meaning:",
    "  - Prompt / content policy / image policy / moderation / review related -> the user's prompt (or reference image/video) did not pass moderation; ask them to modify and retry; note this is usually caused by {{provider}}'s own moderation and FlowMuse cannot intervene.",
    "  - Timeout related -> the task timed out; ask the user to resubmit.",
    "  - 500 / Internal Server Error related -> the generation service is busy; ask the user to retry later; note this is usually {{provider}} being busy/overloaded and FlowMuse cannot intervene.",
    "  - Other network errors -> the network connection failed; ask the user to refresh and retry.",
    "  - Undefined / unknown -> an unknown error occurred; ask the user to contact support if it persists.",
    "  - Node not found related -> ask the user to wait for the canvas to finish saving, then retry.",
    "  - Invalid URL format related -> ask the user to provide a publicly accessible share link.",
    "  - Anything else -> generation failed; ask the user to retry later.",
    "Write the entire final message in {{lang}}.",
].join('\n');

const ERROR_LANG_NAME: Record<string, string> = {
    zh: 'Simplified Chinese',
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    ja: 'Japanese',
    ko: 'Korean',
    pt: 'Portuguese',
};

/** 归一化为受支持的目标语言代码（缺省 zh 兼容旧行为；无法识别的值回退英文）。 */
function normalizeErrorLocale(locale?: string): keyof typeof ERROR_LANG_NAME {
    const s = (locale ?? '').trim().toLowerCase();
    if (!s) return 'zh';
    for (const key of Object.keys(ERROR_LANG_NAME)) {
        if (s.startsWith(key)) return key;
    }
    return 'en';
}

/**
 * 把技术错误改写为面向用户的友好提示，按 locale 输出对应语言。
 * @param locale 目标语言（支持 en/zh/es/fr/de/ja/ko/pt，默认 'zh'，无法识别回退英文）。
 * @param systemPrompt 可选自定义系统提示词（支持 {{provider}} / {{lang}} 占位符）；不传用 DEFAULT。
 */
export async function translateError(
    input: string,
    locale: string = 'zh',
    provider?: string,
    systemPrompt?: string,
): Promise<string> {
    const trimmed = (input ?? '').trim();
    if (!trimmed) return '';

    const lang = normalizeErrorLocale(locale);
    const langName = ERROR_LANG_NAME[lang];

    // 目标为中文且输入已是纯中文：直接返回（保持既有行为，省一次调用）。
    const hasAsciiLetters = /[A-Za-z]/.test(trimmed);
    const hasCjk = /[\u4e00-\u9fff]/.test(trimmed);
    if (lang === 'zh' && !hasAsciiLetters && hasCjk) return trimmed;

    const system = (systemPrompt || DEFAULT_ERROR_TRANSLATE_PROMPT)
        .replace(/\{\{provider\}\}/g, provider ?? '')
        .replace(/\{\{lang\}\}/g, langName);

    const prompt = [
        `Target language: ${langName}`,
        `Provider: ${provider ?? ''}`,
        `Text: ${trimmed}`,
    ].join('\n');

    const { text } = await generateText({
        model: TRANSLATE_MODEL,
        temperature: 0,
        system: system,
        prompt: prompt,
        abortSignal: AbortSignal.timeout(TRANSLATE_TIMEOUT_MS),
    });

    return text.trim();
}